import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AssetService } from '~/app/services/asset/asset.service';
import type { GetKmsDocumentStatusResponse } from '~/app/services/kms/kms-document.types';
import {
  normalizeAssetLinkCandidates,
  resolveKmsLinkerCandidates,
} from '~/app/services/kms/kms-document.utils';

export type KmsAssetLinkCandidateRow = {
  key: string;
  assetId: string;
  score: number;
  reason?: string;
  serialLabel: string;
  assetName: string | null;
};

export function useKmsAssetLinkCandidates(params: {
  document: GetKmsDocumentStatusResponse | null;
  organizationId: string | null;
}) {
  const { document, organizationId } = params;
  const { t } = useTranslation('kms');

  const candidates = useMemo(
    () =>
      document
        ? normalizeAssetLinkCandidates(resolveKmsLinkerCandidates(document))
        : [],
    [document],
  );

  const assetsQuery = useQuery({
    queryKey: ['kms', 'asset-link-candidates', organizationId],
    queryFn: () =>
      AssetService.listByOrganization({
        organizationId: organizationId!,
        page: 1,
        limit: 1000,
      }),
    enabled: Boolean(organizationId) && candidates.length > 0,
  });

  const assetById = useMemo(() => {
    const map = new Map<string, { serialNumber: string; assetName: string }>();
    for (const asset of assetsQuery.data?.data ?? []) {
      map.set(asset.id, {
        serialNumber: asset.serialNumber.trim(),
        assetName: asset.assetName,
      });
    }
    return map;
  }, [assetsQuery.data]);

  const rows = useMemo((): KmsAssetLinkCandidateRow[] => {
    return candidates.map((candidate) => {
      const assetId = candidate.assetId!;
      const meta = assetById.get(assetId);
      const serial = meta?.serialNumber ?? '';
      const serialLabel =
        serial.length > 0 ? serial : t('detail.assetLink.serialUnknown');

      return {
        key: candidate.key,
        assetId,
        score: candidate.score,
        reason: candidate.reason,
        serialLabel,
        assetName: meta?.assetName ?? null,
      };
    });
  }, [assetById, candidates, t]);

  return {
    rows,
    isLoading: assetsQuery.isLoading && candidates.length > 0,
    isEmpty: candidates.length === 0,
  };
}
